/**
 * Service to manage files.
 */
import { inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import heic2any from 'heic2any';

export interface ConvertedFile<T> {
  fileUrl: string;
  fileSafeUrl: SafeUrl;
  name: string;
  size: string;
  data?: T;
}

/** Cache used for file conversion. Key is combination of file name and lastModified timestamp */
export type ConvertedFileCache<T> = Record<string, ConvertedFile<T>>

export interface FileToConvert<T> {
  file: File;
  data?: T;
}

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private readonly domSanitizer = inject(DomSanitizer);

  public downloadFile(data: Blob, fileName: string, mimeType = 'application/pdf') {
    // It is necessary to create a new blob object with mime-type explicitly set
    // otherwise only Chrome works like it should
    const newBlob = new Blob([data], { type: mimeType });

    // IE doesn't allow using a blob object directly as link href
    // instead it is necessary to use msSaveOrOpenBlob
    const nav = window.navigator as any;
    if (nav && nav.msSaveOrOpenBlob) {
      nav.msSaveOrOpenBlob(newBlob, fileName);
      return;
    }

    // For other browsers:
    // Create a link pointing to the ObjectURL containing the blob.
    const dataUrl = window.URL.createObjectURL(newBlob);

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = fileName;
    // this is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));

    setTimeout(() => {
      // For Firefox it is necessary to delay revoking the ObjectURL
      window.URL.revokeObjectURL(dataUrl);
      link.remove();
    }, 100);
  }

  /**
   * Reads a file into memory, decoupled from the original OS file. Protects later uploads from
   * ERR_UPLOAD_FILE_CHANGED when the OS mutates the picked file afterward
   */
  async snapshotFile(file: File): Promise<File> {
    const buffer = await file.arrayBuffer();
    return new File([buffer], file.name, { type: file.type, lastModified: file.lastModified });
  }

  /**
   * Converts any .HEIC file so browsers can display it
   */
  convertHeicFiles<T>(files: FileToConvert<T>[], cache?: ConvertedFileCache<T>): Promise<ConvertedFile<T>[]> {
    const work = files.map<Promise<ConvertedFile<T>>>((file) => {
      if (cache && cache[this.getFileCacheKey(file.file)]) {
        return Promise.resolve(cache[this.getFileCacheKey(file.file)]);
      }

      const nonConvertedUrl = URL.createObjectURL(file.file);
      if (file.file.type !== 'image/heic') {
        return Promise.resolve({
          fileUrl: nonConvertedUrl,
          fileSafeUrl: this.domSanitizer.bypassSecurityTrustUrl(nonConvertedUrl),
          name: file.file.name,
          size: this.formatFileSize(file.file.size),
          data: file.data,
        });
      }

      return heic2any({ blob: file.file })
        .then((conversionResult: Blob) => {
          const url = URL.createObjectURL(conversionResult);
          const converted = {
            fileUrl: url,
            fileSafeUrl: this.domSanitizer.bypassSecurityTrustUrl(url),
            name: file.file.name,
            size: this.formatFileSize(file.file.size),
            data: file.data,
          } as ConvertedFile<T>;
          if (cache) {
            cache[this.getFileCacheKey(file.file)] = converted;
          }
          return converted;
        })
        .catch(() => {
          return {
            fileUrl: nonConvertedUrl,
            fileSafeUrl: this.domSanitizer.bypassSecurityTrustUrl(nonConvertedUrl),
            name: file.file.name,
            size: this.formatFileSize(file.file.size),
            data: file.data,
          } as ConvertedFile<T>;
        });
    });

    return Promise.all(work);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileCacheKey(file: File) {
    return `${file.name}-${file.lastModified}`;
  }
}
